<template>
  <q-radio v-bind="$attrs" :size type="radio" :class="classes">
    <span v-if="label" class="tw-truncate tw-pl-1">
      {{ label }}
    </span>

    <q-icon v-if="tooltip" :size name="fa-solid fa-info-circle">
      <q-tooltip anchor="top middle" self="bottom middle"
        >{{ tooltip }}
      </q-tooltip>
    </q-icon>

    <div class="custom-input__check" :class="feedbackIconClasses">
      <q-icon :name="feedbackIcon" class="custom-input__check__icon" />
    </div>
  </q-radio>
</template>

<script setup>
import { computed, watch, ref } from "vue";
import { debounce } from "quasar";

const props = defineProps({
  borderless: {
    type: Boolean,
    default: false,
  },
  feedback: {
    type: Object,
    default: {},
  },
  tooltip: {
    type: String,
    required: false,
    default: "",
  },
  size: {
    type: String,
    default: "xs",
  },
  label: {
    type: String,
    default: () => "",
    required: true,
  },
});

// Refs
const currentFeedback = ref({});

const classes = computed(() => {
  return [
    ...(!props.borderless ? ["border"] : []),
    ...(Object.keys(currentFeedback.value).length
      ? currentFeedback.value?.type === "error"
        ? ["feedback-error"]
        : ["feedback-success"]
      : []),
  ];
});

// Return the correct icon based on the feedbackType
const feedbackIcon = computed(() => {
  const icon = currentFeedback.value.type === "error" ? "fa-xmark" : "fa-check";

  return `fa-solid ${icon}`;
});

// Conditionally add classes based on the feedback
const feedbackIconClasses = computed(() => {
  return [
    ...(currentFeedback.value.type ? ["is-active"] : []),
    ...(currentFeedback.value.type === "error" ? ["tw-bg-red-500"] : []),
    ...(currentFeedback.value.type === "success" ? ["tw-bg-green-500"] : []),
  ];
});

// Remove the errors from the input
const removeFeedback = debounce(() => {
  if (currentFeedback.value.type === "error") {
    return;
  }

  currentFeedback.value = {};
}, 3000);

// Watch errors
watch(
  () => props.feedback,
  (feedback) => {
    currentFeedback.value = feedback;

    removeFeedback();
  },
);
</script>

<style lang="scss">
div.q-radio {
  @apply tw-h-10 tw-overflow-hidden tw-rounded-lg tw-pl-[6px] tw-pr-3;
  display: flex !important;
  transition: border $generic-hover-transition;
  padding-right: 4px;
  position: relative;
  overflow: hidden;

  &:not(.border) {
    @apply tw-border-none;
  }

  &.border {
    @apply tw-border tw-border-gray-200 tw-transition;

    &:hover {
      @apply tw-border-primary;
    }

    &.feedback-success {
      @apply tw-border-green-500;
    }

    &.feedback-error {
      @apply tw-border-red-500;
    }
  }

  &.feedback-error {
    @apply tw-bg-red-50;

    .q-radio__inner {
      @apply tw-text-red-500;
    }
  }

  &.feedback-success {
    @apply tw-bg-green-50;

    .q-radio__inner {
      @apply tw-text-green-500;
    }
  }

  .q-radio__inner--truthy {
    @apply tw-text-green;
  }

  &.disabled {
    &.border:hover {
      border-color: inherit !important;
    }
  }

  &:has(.q-radio__label:not(:empty)) {
    @apply tw-flex tw-pr-2;
  }

  .q-radio__inner {
    @apply tw-transition-all tw-duration-200;
  }

  .q-radio__label:has(.q-icon) {
    @apply tw-flex tw-flex-1 tw-items-center tw-justify-between tw-overflow-hidden;
  }

  .custom-input__check {
    @apply tw-flex tw-items-center tw-justify-center tw-rounded-bl tw-transition-all tw-duration-200;
    width: 24px;
    height: 24px;
    position: absolute;
    top: 0;
    right: 0;
    transform: translate3d(102%, -102%, 0);

    &.is-active {
      transform: translate3d(0, 0, 0);
    }

    &__icon {
      @apply tw-text-white;
    }
  }
}
</style>
